import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { Banner } from "../components/ui/Banner";
import { ButtonLink, Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Logo } from "../components/ui/Logo";
import { UploadsProvider } from "../features/videos/uploads";
import { useMe, useSession } from "./session";
import { useWorkspace } from "./workspace";

const NAV = [
  { to: "/streams", label: "streams" },
  { to: "/videos", label: "videos" },
  { to: "/billing", label: "billing" },
  { to: "/settings", label: "settings" },
] as const;

function WorkspacePicker() {
  const { workspace, workspaces, select } = useWorkspace();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (workspaces.length < 2) return <span className="text-muted">{workspace.name}</span>;
  return (
    <label className="flex items-center gap-2 text-muted">
      workspace
      <select
        value={workspace.id}
        onChange={(e) => {
          select(e.target.value);
          // A detail page belongs to the old workspace; go back to the section's list.
          navigate(`/${pathname.split("/")[1] || "streams"}`);
        }}
        className="min-h-10 rounded-full border-2 border-ink bg-card px-3 py-1.5 text-ink"
      >
        {workspaces.map((w) => <option key={w.id} value={w.id}>{w.isPersonal ? `${w.name} (personal)` : w.name}</option>)}
      </select>
    </label>
  );
}

export function AppShell() {
  const me = useMe();
  const { pathname } = useLocation();
  const { signOut } = useSession();
  const { workspace, writable, can } = useWorkspace();

  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-30 focus:rounded-lg focus:bg-ink focus:p-2 focus:text-white">skip to content</a>
      <header className="sticky top-0 z-20 border-b-2 border-ink bg-card/85 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-6">
          <NavLink to="/streams" className="no-underline hover:text-ink"><Logo /></NavLink>
          <nav aria-label="Main" className="flex flex-auto flex-wrap gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => cx(
                  "rounded-full border-2 px-3.5 py-1.5 font-medium no-underline motion-safe:transition-colors motion-safe:duration-300",
                  isActive ? "border-ink bg-ink text-white shadow-hard-sm hover:text-white" : "border-transparent text-muted hover:border-ink hover:bg-paper hover:text-ink",
                )}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <WorkspacePicker />
          <div className="flex items-center gap-2">
            <span className="hidden text-muted md:inline" title={me.user.email}>{me.user.name}</span>
            <Button size="sm" variant="ghost" onClick={() => void signOut()}>sign out</Button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto flex max-w-6xl flex-col gap-8 px-4 pt-10 pb-16 sm:px-6">
        {!writable && (
          <Banner
            tone="warn"
            action={can("billing.manage") && <ButtonLink to="/billing" size="sm" variant="primary">choose a plan</ButtonLink>}
          >
            <strong>{workspace.name}</strong> has no active plan, so it is read-only. You can still stop streams and download videos.
          </Banner>
        )}
        {/* Keyed by workspace so every page starts fresh after a switch, and by path so each page rises in. */}
        <UploadsProvider>
          <div key={pathname} className="page-enter contents">
            <Outlet key={workspace.id} />
          </div>
        </UploadsProvider>
      </main>
    </div>
  );
}
